
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator, TrendingUp, DollarSign, BarChart3, Download, Plus } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

export default function EconomicAssessment() {
  const [analysis, setAnalysis] = useState({
    projectValue: "",
    implementationCost: "",
    operationalCosts: "",
    timeframe: "",
    discountRate: "3.5",
    inflationRate: "2.5"
  });

  const calculateNPV = () => {
    // Basic NPV calculation logic
    const benefits = parseFloat(analysis.projectValue) || 0;
    const costs = parseFloat(analysis.implementationCost) + parseFloat(analysis.operationalCosts) || 0;
    const rate = parseFloat(analysis.discountRate) / 100;
    const years = parseFloat(analysis.timeframe) || 1;
    
    return benefits - costs / Math.pow(1 + rate, years);
  };

  const npv = calculateNPV();
  const roi = npv > 0 ? ((npv / (parseFloat(analysis.implementationCost) || 1)) * 100).toFixed(2) : "0";

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Calculator className="h-8 w-8 text-primary" />
            Economic Assessment
          </h1>
          <p className="text-muted-foreground">
            Comprehensive economic analysis and valuation tools for project assessment
          </p>
        </div>

        <Tabs defaultValue="analysis" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="analysis">Cost-Benefit Analysis</TabsTrigger>
            <TabsTrigger value="npv">NPV Calculator</TabsTrigger>
            <TabsTrigger value="scenarios">Scenario Analysis</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="analysis" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Project Parameters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="projectValue">Total Project Value (£)</Label>
                    <Input
                      id="projectValue"
                      type="number"
                      placeholder="1000000"
                      value={analysis.projectValue}
                      onChange={(e) => setAnalysis({...analysis, projectValue: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="implementationCost">Implementation Cost (£)</Label>
                    <Input
                      id="implementationCost"
                      type="number"
                      placeholder="500000"
                      value={analysis.implementationCost}
                      onChange={(e) => setAnalysis({...analysis, implementationCost: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="operationalCosts">Annual Operational Costs (£)</Label>
                    <Input
                      id="operationalCosts"
                      type="number"
                      placeholder="50000"
                      value={analysis.operationalCosts}
                      onChange={(e) => setAnalysis({...analysis, operationalCosts: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="timeframe">Project Timeframe (Years)</Label>
                    <Input
                      id="timeframe"
                      type="number"
                      placeholder="5"
                      value={analysis.timeframe}
                      onChange={(e) => setAnalysis({...analysis, timeframe: e.target.value})}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Economic Indicators
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="discountRate">Discount Rate (%)</Label>
                    <Select value={analysis.discountRate} onValueChange={(value) => setAnalysis({...analysis, discountRate: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2.5">2.5%</SelectItem>
                        <SelectItem value="3.5">3.5% (HM Treasury Green Book)</SelectItem>
                        <SelectItem value="5.0">5.0%</SelectItem>
                        <SelectItem value="7.0">7.0%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="inflationRate">Inflation Rate (%)</Label>
                    <Input
                      id="inflationRate"
                      type="number"
                      step="0.1"
                      value={analysis.inflationRate}
                      onChange={(e) => setAnalysis({...analysis, inflationRate: e.target.value})}
                    />
                  </div>
                  <div className="pt-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Net Present Value:</span>
                      <span className={`font-bold ${npv >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        £{npv.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Return on Investment:</span>
                      <span className="font-bold text-primary">{roi}%</span>
                    </div>
                    <Progress value={Math.min(parseFloat(roi), 100)} className="w-full" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="npv" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>NPV Calculator Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">£{npv.toLocaleString()}</div>
                        <p className="text-sm text-muted-foreground">Net Present Value</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-primary">{roi}%</div>
                        <p className="text-sm text-muted-foreground">Return on Investment</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {analysis.timeframe || 0} years
                        </div>
                        <p className="text-sm text-muted-foreground">Payback Period</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Sensitivity Analysis</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Benefits increase by 10%</Label>
                      <div className="p-3 bg-green-50 rounded-lg">
                        NPV: £{(npv * 1.1).toLocaleString()}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Costs increase by 10%</Label>
                      <div className="p-3 bg-red-50 rounded-lg">
                        NPV: £{(npv * 0.9).toLocaleString()}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="scenarios" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Scenario Analysis</span>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Scenario
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="border-green-200">
                      <CardHeader>
                        <CardTitle className="text-green-700">Optimistic</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Benefits:</span>
                            <span>+20%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Costs:</span>
                            <span>-10%</span>
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>NPV:</span>
                            <span className="text-green-600">£{(npv * 1.3).toLocaleString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-blue-200">
                      <CardHeader>
                        <CardTitle className="text-blue-700">Most Likely</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Benefits:</span>
                            <span>Baseline</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Costs:</span>
                            <span>Baseline</span>
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>NPV:</span>
                            <span className="text-blue-600">£{npv.toLocaleString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="border-red-200">
                      <CardHeader>
                        <CardTitle className="text-red-700">Pessimistic</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span>Benefits:</span>
                            <span>-20%</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Costs:</span>
                            <span>+15%</span>
                          </div>
                          <div className="flex justify-between font-bold">
                            <span>NPV:</span>
                            <span className="text-red-600">£{(npv * 0.65).toLocaleString()}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Economic Assessment Reports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>NPV Analysis Report</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Cost-Benefit Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Scenario Comparison</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Sensitivity Analysis</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
